
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import DateFilter from '@/components/reports-v3/components/filters/DateFilter.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import moment from 'moment';
import { GobiColor } from '@/helpers/color';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import {getTableName, TIMEZONE_OFFSET_MS} from '@/worker/db/sqlHelpers';

@Component({
	components: {
		StackedBarChartFilterable,
		DateFilter,
	},
})
export default class CustomerPaymentAnalysisBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public filterIds: Array<
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'paymentAnalysisYear'
	> = ['customers', 'agents', 'paymentAnalysisYear'];

	public xAxisData: string[] = [];
	public series: echarts.ECharts[] = [];

	public selectedStartDateOfYear: number = moment().startOf('year').valueOf();

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get tooltipFormatter() {
		return TooltipFormatter.basicWithStackShare;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.basicWithRowShare;
	}

	public get expensiveHook() {
		const { selectedStartDateOfYear, selectedAgents, selectedCustomers } = this;
		return JSON.stringify([
			selectedStartDateOfYear,
			selectedAgents,
			selectedCustomers,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const kosTable = getTableName('customerKos');
		const msInDay = 24 * 60 * 60 * 1000;
		const year = moment(this.selectedStartDateOfYear).year();

		let customerFilter = '';
		if (this.selectedCustomers.length > 0) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['customerKos']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		// dueLen = FROMDOCDATE - TODOCDUEDATE (in ms)
		// amountGainLoss = KOLOCALAMT + KOGAINLOSS
		const rows = await queryHead.rawQuery(`
			SELECT
				EXTRACT(MONTH FROM EPOCH_MS(TRY_CAST("TODOCDUEDATE" AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as mth,
				CASE
					WHEN (TRY_CAST("FROMDOCDATE" AS BIGINT) - TRY_CAST("TODOCDUEDATE" AS BIGINT)) <= 0 THEN 'On Time'
					WHEN (TRY_CAST("FROMDOCDATE" AS BIGINT) - TRY_CAST("TODOCDUEDATE" AS BIGINT)) <= ${30 * msInDay} THEN '30 Days'
					ELSE 'Over 30 Days'
				END as bucket,
				SUM(TRY_CAST("KOLOCALAMT" AS DOUBLE) + TRY_CAST("KOGAINLOSS" AS DOUBLE)) as amt
			FROM ${kosTable}
			WHERE EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST("TODOCDUEDATE" AS BIGINT) + ${TIMEZONE_OFFSET_MS})) = ${year}
				${customerFilter}${agentFilter}
			GROUP BY mth, bucket
			ORDER BY mth
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9);

		const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
			'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		this.xAxisData = monthNames;

		const bucketNames = ['On Time', '30 Days', 'Over 30 Days'];
		const bucketColors = [GobiColor.COLOR1, GobiColor.COLOR2, GobiColor.COLOR3];

		// Build monthly data per bucket
		const bucketData: Record<string, number[]> = {};
		for (const b of bucketNames) {
			bucketData[b] = new Array(12).fill(0);
		}
		for (const row of rows) {
			const mth = Number(row.mth) || 1;
			const bucket = row.bucket as string;
			if (bucketData[bucket]) {
				bucketData[bucket][mth - 1] = Number(row.amt) || 0;
			}
		}

		this.series = bucketNames.map((name, i) => ({
			name,
			type: 'bar',
			stack: 'a',
			barWidth: '40%',
			color: bucketColors[i],
			data: bucketData[name],
		})) as any;

		this.generateLoadingText(1);
		this.saveHistory('series', 'xAxisData');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
